import React from 'react'
import { DateTime } from 'luxon'
import { groupBy } from 'lodash-es'
import Alerts from './alerts'
import Hours from './hours'
import Events from './events'
import AnimalEncounters from './animal-encounters'
import FourDExperiences from './4d-experiences'
import DateRange from '../../entities/date-range'
import DateRangeFormatter from '../../helpers/date-range-formatter'
import {
  OCEANARIUM_SHOW,
  CALENDAR_EVENT,
  PROD_CALENDAR_EVENT,
  SCHEDULE_ACTIVITY
} from '../../constants'

import { Header, ErrorMessage, Overlay, DataProvider } from '../../components'
import { Loading, LoadingIcon } from '../../../shared/components'
import { routerProps } from '../../services/router-props'

function shouldShowEvent(event) {
  let { type, resourceType } = event

  switch (resourceType) {
    case OCEANARIUM_SHOW:
    case CALENDAR_EVENT:
    case PROD_CALENDAR_EVENT:
    case SCHEDULE_ACTIVITY:
      return true
    default:
      return type === 'activity'
  }
}

function filmsByTitle(entries) {
  return groupBy(
    entries.filter(e => e.resourceType && e.resourceType === 'FourDFilm'),
    film => film.title
  )
}

class Day extends React.Component {
  state = {
    date: null,
    data: null,
    loading: true,
    error: null,
    dateRange: null
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.date !== nextProps.date) {
      let date = DateTime.fromISO(nextProps.date, { zone: 'America/Chicago' })

      return {
        dateRange: new DateRange(date),
        date: nextProps.date
      }
    }

    return null
  }

  navigate = date => {
    this.props.navigate(`/calendar/day/${date}`)
  }

  render() {
    let { navigate } = this.props
    let { loading, dateRange } = this.state

    return (
      <React.Fragment key="rootFragment">
        <DataProvider
          dateRange={dateRange}
          onRequest={this.handleLoadStart}
          onSuccess={this.handleSuccess}
          onError={this.handleError}
        />

        <Header
          key="header"
          dateRange={dateRange}
          navigate={navigate}
          navigateRelative={this.navigate}
        >
          <LoadingIcon visible={loading} />
        </Header>

        <Overlay visible={loading}>{this.renderDayView()}</Overlay>
      </React.Fragment>
    )
  }

  renderDayView() {
    let { data, error, loading, dateRange } = this.state

    if (!dateRange || (loading && !data)) {
      return <Loading />
    }

    if (error) {
      return <ErrorMessage />
    }

    let { entries, alerts, aquariumHours, animalEncounters } = data
    let events = entries.filter(shouldShowEvent)
    let films = filmsByTitle(entries)

    if (aquariumHours.openTime === null && aquariumHours.closeTime === null) {
      if (alerts.length === 0) {
        alerts = [
          {
            description:
              'Please select another day to plan your visit to Shedd!',
            name: 'Shedd is closed today'
          }
        ]
      }

      return (
        <div className="container max-w-xl">
          <Alerts alerts={alerts} />
        </div>
      )
    }

    return (
      <div className="container max-w-xl">
        <p
          role="status"
          aria-live="polite"
          aria-atomic={true}
          className="sr-only"
        >
          Events loaded for{' '}
          {DateRangeFormatter.toShortString(this.state.dateRange)}
        </p>

        <Alerts alerts={alerts} />

        <div className="today-grid mb-60 md:mb-120">
          <Hours
            time={aquariumHours.openTime}
            type="Open"
          />
          <Events events={events} />
          <Hours
            time={aquariumHours.closeTime}
            type="Close"
          />
        </div>

        <AnimalEncounters encounters={animalEncounters} />
        <FourDExperiences films={films} />
      </div>
    )
  }

  handleLoadStart = () => {
    this.setState({ loading: true })
  }

  handleSuccess = data => {
    this.setState({ loading: false, data })
  }

  handleError = error => {
    this.setState({ loading: false, error })
  }
}

export default routerProps(Day)
